import React, { useState, useEffect, useRef } from 'react'
import Slider from 'react-slick'
import ReactMarkdown from 'utils/markdown'
import SectionType from 'models/types/ts/sectionType'
import ContentType from 'models/types/ts/contentType'
import { FeatureVideo } from 'features'
import { HeadingUnderline2 } from 'layouts/headings'
import { prev, next } from 'layouts/carousel/helpers'
import Arrows from 'layouts/carousel/components/arrows'
import { colors } from 'config/styled-theme'
import Arrow from 'layouts/arrow'
import { Translate } from 'utils/localize/react-i18next'
import Dots from 'layouts/dots'
import style from './VideoCarousel.module.scss'

interface Props {
    section: SectionType
    color?: string
    playButtonColor?: string
    displayHeadingsLine?: boolean
    CTAButton?: React.ReactNode
}

const VideoCarousel = ({
    section,
    displayHeadingsLine = true,
    color = colors.academy,
    playButtonColor,
    CTAButton,
}: Props): JSX.Element => {
    const { headline, contentCollection } = section
    const [nav1, setNav1] = useState()
    const [nav2, setNav2] = useState()
    const [useTransform, setTransform] = useState(true)

    const slider1 = useRef(null)
    const slider2 = useRef(null)

    // set nav to ref when ready
    useEffect(() => {
        setNav1(slider1.current)
        setNav2(slider2.current)
    }, [])

    const isPlaying = (playing: boolean): void => {
        if (playing) {
            setTransform(false)
        } else {
            setTransform(true)
        }
    }

    const handleClick = (key: number): void => {
        slider1.current.slickGoTo(key)
    }

    function renderHeading(children: React.ReactNode): JSX.Element {
        return (
            <HeadingUnderline2
                color={color}
                title={children}
                displayHeadingsLine={displayHeadingsLine}
            />
        )
    }
    function renderHeadings(): unknown {
        return {
            h1: ({ children }) => renderHeading(children),
            h2: ({ children }) => renderHeading(children),
            h3: ({ children }) => renderHeading(children),
            h4: ({ children }) => renderHeading(children),
            h5: ({ children }) => renderHeading(children),
            h6: ({ children }) => renderHeading(children),
        }
    }

    return (
        <div className={style.carousel}>
            {headline && (
                <div className={style.intro}>
                    <HeadingUnderline2
                        title={headline}
                        color={color}
                        displayHeadingsLine={displayHeadingsLine}
                    />
                </div>
            )}
            {contentCollection.total > 0 && (
                <div className={style.content}>
                    <Slider
                        ref={slider1}
                        asNavFor={nav2}
                        centerPadding="0"
                        arrows={false}
                        useTransform={useTransform}
                        draggable={false}
                        cssEase="cubic-bezier(.5,0,.1,1)"
                        dots
                        appendDots={(dots) => (
                            <Dots className={style.carouselDots} color={color}>
                                {dots}
                            </Dots>
                        )}
                        className={style.slider1}
                    >
                        {contentCollection.items.map(
                            (item: ContentType, key: number) => (
                                <div
                                    key={item.name}
                                    role="presentation"
                                    onClick={() => handleClick(key)}
                                    className={style.video}
                                >
                                    <FeatureVideo
                                        image={item.imageFile?.url}
                                        videoId={item.buttonUrl}
                                        playButtonSize="medium"
                                        playButtonColor={
                                            playButtonColor || color
                                        }
                                        playing={(playing: boolean) =>
                                            isPlaying(playing)
                                        }
                                        transition={false}
                                    />
                                </div>
                            )
                        )}
                    </Slider>
                    <Arrows
                        color={color}
                        prevClick={() => prev(slider1)}
                        nextClick={() => next(slider1)}
                        className={style.arrows}
                    />
                    <Slider
                        ref={slider2}
                        asNavFor={nav1}
                        className={style.slider2}
                        fade
                        draggable={false}
                        arrows={false}
                    >
                        {contentCollection.items.map(
                            (item: ContentType, key: number) => (
                                <div
                                    className={style.text}
                                    key={item.headline}
                                    onClick={() => {
                                        try {
                                            slider1.current.innerSlider.list
                                                .querySelectorAll(
                                                    `[data-index="${key}"]`
                                                )[0]
                                                .querySelectorAll(
                                                    '#video-wrapper'
                                                )[0]
                                                .querySelectorAll('svg')[0]
                                                .dispatchEvent(
                                                    new Event('click', {
                                                        bubbles: true,
                                                    })
                                                )
                                        } catch (e) {
                                            console.error(e)
                                        }
                                    }}
                                >
                                    {item.headline && (
                                        <span
                                            style={{ color }}
                                            className={style.type}
                                        >
                                            {item.headline}
                                        </span>
                                    )}
                                    <ReactMarkdown
                                        source={item.body}
                                        renderers={renderHeadings()}
                                        className={
                                            displayHeadingsLine
                                                ? ''
                                                : style.noMarginTop
                                        }
                                    />
                                    <span
                                        style={{ textTransform: 'uppercase' }}
                                    >
                                        <Translate>
                                            {({ translate }) =>
                                                translate(`dlp.watchVideo`)
                                            }
                                        </Translate>{' '}
                                        <Arrow color={color} />{' '}
                                    </span>
                                    {CTAButton}
                                </div>
                            )
                        )}
                    </Slider>
                </div>
            )}
        </div>
    )
}

export default VideoCarousel
